import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Rating,
} from "@mui/material";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getBestSellingProducts } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";
import { getGradientText, getCardHover, animations } from "../../../../../theme/luxuryTheme";

const BestSellersSection = () => {
  const { t, i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getBestSellingProducts();
        if (res && res.success) {
          const list = Array.isArray(res.data) ? res.data : Array.isArray(res) ? res : [];
          setProducts(list.slice(0, 8)); // عرض 8 منتجات فقط في الصفحة الرئيسية
        } else {
          setProducts([]);
        }
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getName = (p) => (lang === "ar" ? p.name_ar || p.name_en : p.name_en || p.name_ar);
  const getImage = (p) =>
    getImageUrl(p.main_photo || (p.images && p.images[0] && p.images[0].image) || "");

  // new helpers: read selected country_id and pick matching currency entry
  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfo = (product) => {
    if (!product) return null;
    const countryId = getSelectedCountryId();
    const curr = product.currencies || product.Currencies || [];
    let entry = null;
    if (countryId) {
      entry = curr.find((c) => String(c.country_id) === String(countryId));
    }
    if (!entry && curr.length) entry = curr[0];
    if (!entry) {
      const fallback = product.price || product.price_after_discount || null;
      return fallback
        ? { price: String(product.price || ""), price_after_discount: product.price_after_discount ? String(product.price_after_discount) : null }
        : null;
    }
    const rawPrice = entry.price != null ? String(entry.price) : null;
    const rawDiscount =
      entry.price_after_discount != null
        ? String(entry.price_after_discount)
        : null;

    const priceNum = parseFloat(rawPrice || "");
    const discountNum = parseFloat(rawDiscount || "");
    const hasValidDiscount =
      Number.isFinite(discountNum) &&
      discountNum > 0 &&
      (!Number.isFinite(priceNum) || discountNum < priceNum);

    return {
      price: rawPrice,
      price_after_discount: hasValidDiscount ? rawDiscount : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);

  if (loading) {
    return (
      <Box 
        sx={{ 
          py: 6, 
          display: "flex", 
          justifyContent: "center",
          minHeight: "50vh",
          alignItems: "center",
          background: (theme) => theme.palette.background.gradient
        }}
      >
        <CircularProgress size={50} thickness={4} color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography color="error" variant="h6" sx={{ fontWeight: 500 }}>
          {t("error_loading_best_sellers", {
            defaultValue: lang === "ar" ? "حدث خطأ في تحميل الأكثر مبيعاً" : "Error loading best sellers",
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        py: { xs: 5, md: 7 }, 
        px: 2,
        background: (theme) => theme.palette.background.gradient,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, transparent 0%, #d4af37 50%, transparent 100%)",
        }
      }}
    >
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box 
          sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            mb: 5,
            pb: 3,
            borderBottom: "3px solid",
            borderImage: "linear-gradient(90deg, #d4af37 0%, #f4e5c2 50%, #d4af37 100%) 1",
            flexWrap: "wrap",
            gap: 2
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                background: (theme) => theme.palette.gradient.fire,
                borderRadius: "50%",
                p: 1.5,
                display: "flex",
                boxShadow: "0 4px 20px rgba(255, 107, 107, 0.4)",
                ...animations.pulse
              }}
            >
              <LocalFireDepartmentIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Typography 
              variant="h4" 
              fontWeight="800" 
              sx={getGradientText("dark")}
            >
              {t("best_sellers_title", { defaultValue: lang === "ar" ? "الأكثر مبيعاً" : "Best Sellers" })}
            </Typography>
          </Box>

          <Box
            component={Link}
            to="/best-sellers"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "primary.main",
              textDecoration: "none",
              fontWeight: "bold",
              fontSize: "1.1rem",
              transition: "all 0.3s ease",
              "&:hover": {
                gap: 1.5,
                color: "primary.dark",
              }
            }}
          >
            {t("view_all", { defaultValue: lang === "ar" ? "عرض الكل" : "View all" })}
            <ArrowForwardIcon sx={{ transform: lang === "ar" ? "rotate(180deg)" : "none" }} />
          </Box>
        </Box>

        <Grid container spacing={3}>
          {products.map((product) => {
            const pinfo = getPriceInfo(product);
            const originalNum = parseFloat(pinfo?.price || "");
            const discountedNum = parseFloat(pinfo?.price_after_discount || "");
            const hasDiscount =
              !!pinfo?.price_after_discount &&
              Number.isFinite(originalNum) &&
              Number.isFinite(discountedNum) &&
              discountedNum > 0 &&
              discountedNum < originalNum;
            const discountPercent = hasDiscount
              ? Math.round(((originalNum - discountedNum) / originalNum) * 100)
              : 0;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card 
                  sx={{ 
                    height: "100%",
                    position: "relative",
                    overflow: "visible",
                    background: "#fff",
                    ...getCardHover()
                  }}
                >
                  {hasDiscount && (
                    <Chip
                      icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
                      label={`-${discountPercent}%`}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: lang === "ar" ? "auto" : 12,
                        left: lang === "ar" ? 12 : "auto",
                        zIndex: 2,
                        background: (theme) => theme.palette.gradient.red,
                        color: "#fff",
                        fontWeight: "800",
                        fontSize: "0.8rem",
                        boxShadow: (theme) => theme.shadows[15],
                        border: "2px solid #fff"
                      }}
                    />
                  )}

                  <CardActionArea
                    component={Link}
                    to={`/product/${product.id}`}
                    sx={{ 
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      p: 2.5
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: "100%",
                        height: 200,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        overflow: "hidden",
                        borderRadius: 2,
                        background: (theme) => theme.palette.gradient.subtleGray,
                        position: "relative"
                      }}
                    >
                      <Box 
                        component="img" 
                        src={getImage(product)} 
                        alt={getName(product)} 
                        className="product-image"
                        sx={{ 
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                        }} 
                      />
                      <Box
                        className="view-details"
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: "linear-gradient(0deg, rgba(44,62,80,0.9) 0%, transparent 100%)",
                          color: "#fff",
                          py: 1,
                          textAlign: "center",
                          opacity: 0,
                          transform: "translateY(10px)",
                          transition: "all 0.3s ease",
                          fontSize: "0.85rem",
                          fontWeight: 600
                        }}
                      >
                        {lang === "ar" ? "عرض التفاصيل" : "View Details"}
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, textAlign: "center", width: "100%" }}>
                      <Typography 
                        variant="h6" 
                        fontWeight="700" 
                        sx={{
                          mb: 1.5,
                          minHeight: 48,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          color: "secondary.main",
                          lineHeight: 1.3,
                          fontSize: "1rem"
                        }}
                      >
                        {getName(product)}
                      </Typography>

                      <Box sx={{ mb: 1.5 }}>
                        {(() => {
                          if (!pinfo) return null;
                          const orig = formatPrice(pinfo.price);
                          const disc = formatPrice(pinfo.price_after_discount);
                          return (
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                              {disc ? (
                                <>
                                  <Typography 
                                    variant="h5" 
                                    sx={{
                                      fontWeight: "800",
                                      ...getGradientText("gold")
                                    }}
                                  >
                                    {disc} ج.م
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{
                                      textDecoration: "line-through",
                                      color: "text.secondary",
                                      fontWeight: 500
                                    }}
                                  >
                                    {orig} ج.م
                                  </Typography>
                                </>
                              ) : (
                                <Typography 
                                  variant="h5" 
                                  sx={{
                                    fontWeight: "800",
                                    color: "secondary.main"
                                  }}
                                >
                                  {orig} ج.م
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </Box>

                      <Chip
                        label={
                          product.order_count
                            ? `${lang === "ar" ? "تم بيع" : "Sold"} ${product.order_count}`
                            : lang === "ar" ? "شائع" : "Popular"
                        }
                        size="small"
                        sx={{
                          mb: 1.5,
                          background: (theme) => theme.palette.gradient.green,
                          color: "#fff",
                          fontWeight: "700",
                          fontSize: "0.75rem",
                          boxShadow: (theme) => theme.shadows[17]
                        }}
                      />

                      {product.reviews_summary ? (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <Rating 
                            value={parseFloat(product.reviews_summary.average_rating) || 0} 
                            readOnly 
                            size="small"
                            precision={0.5}
                          />
                          <Typography variant="caption" color="text.secondary" fontWeight="600">
                            ({product.reviews_summary.total_reviews || 0})
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default BestSellersSection;
